import type { ReactNode } from 'react'

import { Button } from '@/components/ui/button'
import { Slider } from '@/components/ui/slider'
import { AmountInput } from './AmountInput'
import { formatNumber } from '../utils/format'

const PRESETS = [10, 25, 50, 100]

export interface ShareOfVoiceInputProps {
  /** Percentage as a string, so a half-typed value survives round-tripping. */
  value: string
  onChange: (value: string) => void
  /** Accessible name for the slider; the visible label sits outside. */
  label: string
  /** The line under the slider explaining what the chosen share means. */
  summary: (share: number) => ReactNode
  placeholder?: string
  /** Empty means "not set", so dragging the slider to 0 clears the field. */
  optional?: boolean
  disabled?: boolean
}

/**
 * Picks a share of voice, 0–100%. The slider and presets cover the usual
 * round numbers; the field takes an exact figure.
 */
export function ShareOfVoiceInput({
  value,
  onChange,
  label,
  summary,
  placeholder = '0',
  optional,
  disabled,
}: ShareOfVoiceInputProps) {
  const parsed = parseFloat(value)
  const share = isNaN(parsed) ? 0 : Math.min(Math.max(parsed, 0), 100)

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-4">
        <Slider
          value={[share]}
          onValueChange={([v]) => onChange(optional && v === 0 ? '' : String(v))}
          min={0}
          max={100}
          step={1}
          disabled={disabled}
          aria-label={label}
          className="flex-1"
        />
        <AmountInput
          value={value}
          onChange={onChange}
          unit="%"
          max={100}
          placeholder={placeholder}
          disabled={disabled}
          className="w-28"
        />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-1.5">
          {PRESETS.map((preset) => (
            <Button
              key={preset}
              type="button"
              size="xs"
              variant={share === preset ? 'default' : 'outline'}
              onClick={() => onChange(String(preset))}
              disabled={disabled}
            >
              {formatNumber(preset)}%
            </Button>
          ))}
        </div>
        <span className="text-xs text-(--ad-text-muted)">{summary(share)}</span>
      </div>
    </div>
  )
}
